/**
 * Update summary panel for displaying recent updates for a company.
 * Shows summary text, counts by section, and individual update items.
 */

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

const materialityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-red-50 text-red-700 border-red-100',
  medium: 'bg-amber-50 text-amber-700 border-amber-100',
  low: 'bg-gray-50 text-gray-600 border-gray-200',
};

const sectionIcons: Record<string, JSX.Element> = {
  news: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  ),
  leadership: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  products: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  fundamentals: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Just now';
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

export function UpdateSummaryPanel() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['updateSummary', companyId],
    queryFn: () => api.getCompanyUpdateSummary(companyId!),
    enabled: !!companyId,
  });

  const markViewedMutation = useMutation({
    mutationFn: () => api.markSummaryViewed(companyId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyUpdates'] });
    },
  });

  // Automatically mark updates as consumed when user views this page
  const hasMarkedViewed = useRef(false);
  useEffect(() => {
    if (summary && summary.total_unread > 0 && !hasMarkedViewed.current) {
      hasMarkedViewed.current = true;
      markViewedMutation.mutate();
    }
  }, [summary, markViewedMutation]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>Failed to load updates</p>
        <Link
          to={`/companies/${companyId}`}
          className="text-primary-600 hover:underline mt-2 inline-block"
        >
          Back to company
        </Link>
      </div>
    );
  }

  const handleBack = () => {
    navigate(`/companies/${companyId}`);
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={handleBack}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm mb-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to {summary.company_name}
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Recent Updates</h1>
          <p className="text-gray-500 mt-1">{summary.company_name}</p>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-medium text-gray-900">
              {summary.total_unread > 0 ? summary.summary_text : 'All caught up!'}
            </p>
            {summary.highest_materiality && (
              <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded border ${materialityColors[summary.highest_materiality]}`}>
                {summary.highest_materiality.charAt(0).toUpperCase() + summary.highest_materiality.slice(1)} priority
              </span>
            )}
          </div>
          <div className="text-4xl font-bold text-primary-600">
            {summary.total_unread}
          </div>
        </div>

        {/* Section breakdown */}
        {summary.total_unread > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex gap-4">
            {Object.entries(summary.by_section).map(([section, count]) => (
              count > 0 && (
                <div key={section} className="flex items-center gap-2 text-sm text-gray-600">
                  {sectionIcons[section]}
                  <span className="capitalize">{section}:</span>
                  <span className="font-medium">{count}</span>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* Update Items */}
      {summary.items.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Update Details
          </h2>
          {summary.items.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-lg shadow-sm border p-4 ${materialityColors[item.materiality] || 'border-gray-200'}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-gray-400">
                  {sectionIcons[item.section] || sectionIcons.news}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase">
                      {item.section}
                    </span>
                    <span className="text-gray-300">|</span>
                    <span className="text-xs text-gray-400">
                      {item.update_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-gray-900 font-medium">{item.title}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatRelativeTime(item.timestamp)}
                  </p>
                </div>
                <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded ${materialityColors[item.materiality]}`}>
                  {item.materiality}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No pending updates</p>
          <p className="text-sm mt-1">You're all caught up with {summary.company_name}</p>
        </div>
      )}
    </div>
  );
}

export default UpdateSummaryPanel;
